/**
 * Para ve miktar aritmetiği — ADR-03 ve §6.1.
 *
 * Kırmızı çizgi: bu dosya, ham `string`/`number` değerlerin Decimal'e çevrildiği
 * TEK yerdir. Başka hiçbir modül `Number()`, `parseFloat()` veya unary `+`
 * kullanamaz; ESLint bunu derleme kırarak engeller (bkz. eslint.config.js).
 *
 * - Para  -> DB'de numeric(12,2)  -> 2 ondalık, ROUND_HALF_UP
 * - Miktar-> DB'de numeric(12,3)  -> 3 ondalık, ROUND_HALF_UP
 *
 * JSON'a her zaman string olarak serileştirilir ("50.00"), asla number olarak değil.
 * Bir kez `number`a düşen para değeri, geri dönüşü olmayan şekilde kuruş kaybeder.
 */
// NOT: `import Decimal from 'decimal.js'` KULLANILMAZ. decimal.js'in tip
// tanımlarında default export, ESM çözümlemesinde sınıf yerine namespace'e
// bağlanıyor ve `new Decimal(...)` derlenmiyor. Adlandırılmış import hem
// decimal.mjs'te gerçekten var hem de kütüphanenin dokümante ettiği biçim.
import { Decimal } from "decimal.js";
Decimal.set({
    precision: 34,
    rounding: Decimal.ROUND_HALF_UP,
    toExpNeg: -9e15,
    toExpPos: 9e15,
});
/** numeric(12,2) — kuruş hassasiyeti */
export const PARA_ONDALIK = 2;
/** numeric(12,3) — gram/adet hassasiyeti */
export const MIKTAR_ONDALIK = 3;
/** numeric(12,x) sınırları: mutlak değer 10^10'dan küçük olmalı. */
const NUMERIC_12_TAVAN = new Decimal("10000000000");
export class ParaHatasi extends Error {
    constructor(mesaj) {
        super(mesaj);
        this.name = "ParaHatasi";
    }
}
function ham(deger, alan) {
    if (deger instanceof Decimal)
        return deger;
    if (typeof deger === "string") {
        const kirpilmis = deger.trim();
        if (kirpilmis === "")
            throw new ParaHatasi(`${alan}: boş değer sayıya çevrilemez`);
        let d;
        try {
            d = new Decimal(kirpilmis);
        }
        catch {
            throw new ParaHatasi(`${alan}: geçersiz sayı "${deger}"`);
        }
        if (!d.isFinite())
            throw new ParaHatasi(`${alan}: sonlu olmayan değer "${deger}"`);
        return d;
    }
    if (typeof deger === "number") {
        // Bilerek reddediyoruz. IEEE-754 double bir para değeri taşıyamaz; bu noktaya
        // gelen bir number, çağıranın bir yerde Number()/parseFloat() kullandığının kanıtıdır.
        throw new ParaHatasi(`${alan}: para/miktar değeri number olarak verilemez (ADR-03). String veya Decimal kullanın.`);
    }
    throw new ParaHatasi(`${alan}: desteklenmeyen tip ${typeof deger}`);
}
/** Ham girdiyi para olarak normalize eder (2 ondalık, yarım yukarı). */
export function para(deger, alan = "tutar") {
    const d = ham(deger, alan).toDecimalPlaces(PARA_ONDALIK, Decimal.ROUND_HALF_UP);
    if (d.abs().gte(NUMERIC_12_TAVAN)) {
        throw new ParaHatasi(`${alan}: numeric(12,2) taşması — ${d.toString()}`);
    }
    return d;
}
/** Ham girdiyi miktar olarak normalize eder (3 ondalık, yarım yukarı). */
export function miktar(deger, alan = "miktar") {
    const d = ham(deger, alan).toDecimalPlaces(MIKTAR_ONDALIK, Decimal.ROUND_HALF_UP);
    if (d.abs().gte(NUMERIC_12_TAVAN)) {
        throw new ParaHatasi(`${alan}: numeric(12,3) taşması — ${d.toString()}`);
    }
    return d;
}
export const SIFIR = para("0");
/** DB'ye ve JSON'a giden kanonik gösterim: her zaman sabit ondalıklı string. */
export function paraStr(deger) {
    return para(deger).toFixed(PARA_ONDALIK);
}
export function miktarStr(deger) {
    return miktar(deger).toFixed(MIKTAR_ONDALIK);
}
export function paraTopla(...degerler) {
    return degerler
        .reduce((acc, d) => acc.plus(para(d)), new Decimal(0))
        .toDecimalPlaces(PARA_ONDALIK, Decimal.ROUND_HALF_UP);
}
export function miktarTopla(...degerler) {
    return degerler
        .reduce((acc, d) => acc.plus(miktar(d)), new Decimal(0))
        .toDecimalPlaces(MIKTAR_ONDALIK, Decimal.ROUND_HALF_UP);
}
/**
 * Satır toplamı: adet x birim fiyat.
 * Yuvarlama SATIR seviyesinde yapılır; böylece satırların toplamı ile
 * sepet toplamı her zaman birebir tutar (gün sonu farkının klasik kaynağı).
 */
export function satirToplam(adet, birimFiyat) {
    return miktar(adet, "adet")
        .times(para(birimFiyat, "birim_fiyat"))
        .toDecimalPlaces(PARA_ONDALIK, Decimal.ROUND_HALF_UP);
}
export function esitMi(a, b) {
    return para(a).equals(para(b));
}
/** Türkçe yerel gösterim — yalnızca SUNUM için, asla hesap için. */
export function paraGoster(deger, simge = "₺") {
    const d = para(deger);
    const [tam = "0", kesir = "00"] = d.abs().toFixed(PARA_ONDALIK).split(".");
    const gruplu = tam.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `${d.isNegative() ? "-" : ""}${gruplu},${kesir} ${simge}`;
}
export { Decimal };
//# sourceMappingURL=para.js.map